import { useEffect, useRef } from "react";
import { addDoc, query, where, onSnapshot, updateDoc, doc, orderBy } from "firebase/firestore";
import { dbNotifications, db } from "../firebase";
import { useUser } from "../contexts/UserContext";
import { useNotifications } from "../contexts/NotificationContext";
import { useUserSettings } from "../contexts/UserSettingsContext";

/**
 * Writes a notification document to Firestore for a target user.
 * Call this from any hook/component when you want to notify someone.
 */
export async function sendNotification(
  targetUserId: string,
  title: string,
  body: string,
  fromUserId: string,
  fromUserName: string,
): Promise<void> {
  await addDoc(dbNotifications, {
    targetUserId,
    title,
    body,
    fromUserId,
    fromUserName,
    createdAt: new Date().toISOString(),
    read: false,
  });
}

/**
 * Listens for incoming Firestore notifications for the current user
 * and feeds them into the in-app NotificationContext.
 */
const useSocialNotifications = () => {
  const userContext = useUser();
  const uid = userContext?.userDetails?.user?.uid;
  const { addNotification } = useNotifications();
  const { settings } = useUserSettings();
  const seenIds = useRef<Set<string>>(new Set());
  const initialLoad = useRef(true);

  useEffect(() => {
    if (!uid) return;
    if (!settings.notifications) return;
    if (!(settings.notificationPrefs?.social ?? true)) return;

    const q = query(
      dbNotifications,
      where("targetUserId", "==", uid),
      where("read", "==", false),
    );

    const unsub = onSnapshot(q, (snap) => {
      for (const change of snap.docChanges()) {
        if (change.type === "added") {
          const data = change.doc.data();
          const notifId = change.doc.id;

          if (seenIds.current.has(notifId)) continue;
          seenIds.current.add(notifId);

          // Show as in-app notification
          addNotification(data.title, data.body, "social");

          // Mark as read in Firestore so it doesn't re-appear
          updateDoc(doc(db, "notifications", notifId), { read: true }).catch(
            () => {},
          );
        }
      }
      initialLoad.current = false;
    });

    return () => unsub();
  }, [uid, settings.notifications, settings.notificationPrefs, addNotification]);
};

export default useSocialNotifications;
